import React, { useState, useEffect, useRef } from "react";
import {
  defaultLangCode,
  langByCode,
  detectLangCode,
  getCachedVoices,
  getVoicesAsync,
  pickVoice,
} from "./voices";

export type ReadOutLouodType = {
  text: string,
  gender?: "male" | "female",
  lang?: string,
};

function toCleanPlainText(input: string): string {
  if (!input) {
    return "";
  }

  return input
    .replace(/\[[A-Z]+\]/g, " ")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s{0,3}>\s?/gm, "")
    .replace(/^\s*([-*+]|\d+\.)\s+/gm, "")
    .replace(/(\*\*|__|\*|_|~~)/g, "")
    .replace(/<[^>]*>/g, " ")
    .replace(
      /(?:\uD83C[\uDDE6-\uDDFF]){1,2}|[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF]|\uFE0F|\u200D/g,
      "",
    )
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function markdownToPlainText(markdown: string): string {
  return toCleanPlainText(markdown);
}

export default function ReadOutLoud(props: ReadOutLouodType) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef(null);
  const keepAliveRef = useRef(null);
  const language = detectLangCode(props.text);
  const bcp47 = props.lang || langByCode[language] || langByCode[defaultLangCode];
  const gender = props.gender === "female" ? "female" : "male";
  const cleanText = markdownToPlainText(props.text);
  const synth = typeof window !== "undefined" ? window.speechSynthesis : null;

  const clearKeepAlive = () => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  };

  useEffect(() => {
    if (!isSpeaking) {
      return undefined;
    }

    const handleBeforeUnload = event => {
      event.preventDefault();
      event.returnValue = "Speech is still playing. Are you sure you want to leave this page?";
      return event.returnValue;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isSpeaking]);

  // preheat the voice cache — in Chrome getVoices() is empty right after a page load, so
  // without this the very first click would fall back to the browser default voice
  useEffect(() => {
    getVoicesAsync();
  }, []);

  useEffect(() => {
    return () => {
      clearKeepAlive();

      if (synth) {
        synth.cancel();
      }

      utteranceRef.current = null;
    };
  }, []);

  const stopSpeaking = () => {
    clearKeepAlive();

    if (synth) {
      synth.cancel();
    }

    utteranceRef.current = null;
    setIsSpeaking(false);
  };

  const startKeepAlive = () => {
    clearKeepAlive();
    keepAliveRef.current = setInterval(() => {
      if (synth && synth.speaking && !synth.paused) {
        synth.resume();
      }
    }, 10000); // stops speaking after ~15s
  };

  const handleClick = () => {
    if (!synth) {
      return;
    }

    if (isSpeaking) {
      stopSpeaking();
      return;
    }

    if (!cleanText) {
      return;
    }

    synth.cancel();

    // A fresh utterance on every click — Safari does not reliably replay the same object,
    // and keeping a live reference prevents Chrome from garbage-collecting it mid-speech.
    const ssu = new SpeechSynthesisUtterance(cleanText);
    utteranceRef.current = ssu;
    ssu.lang = bcp47;

    const voice = pickVoice(getCachedVoices(), bcp47, gender);

    if (voice) {
      ssu.voice = voice;
    } else {
      console.warn(`ReadOutLoud: no voice installed for "${bcp47}", using the browser default`);
    }

    ssu.onstart = () => {
      setIsSpeaking(true);
      startKeepAlive();
    };
    ssu.onend = () => {
      clearKeepAlive();
      utteranceRef.current = null;
      setIsSpeaking(false);
    };
    ssu.onerror = () => {
      clearKeepAlive();
      utteranceRef.current = null;
      setIsSpeaking(false);
    };

    synth.speak(ssu);
  };

  return (
    <span title={language} onClick={handleClick} className="material-icons pointer">
      {isSpeaking ? "stop" : "record_voice_over"}
    </span>
  );
}
